/**
 * @fileoverview The gardener's own to-dos (D50, batch 6 of the to-do rebuild):
 * adding one and editing one. Both are answered as a to-do mark is (api/todo,
 * B49 and B73): at once, once saved, with what to say (said: the toast, its
 * Undo, and openKey, the new or edited to-do's key, which its group shows even
 * past "more") and no brief; the page reads Today again in the background. Deleting one and its
 * Undo are the marks remove and unremove (api/todo markTodo); ticking,
 * postponing and skipping are the marks every job has.
 *
 * The server decides everything: where a to-do goes, its pill, its reason
 * line and the toast's words. The app sends what the gardener typed and
 * picked, and how the brief was asked for.
 *
 * Where: features/own-todos (useOwnTodos).
 * API: POST /todo/own { title, when, day?, about, plantObjId?, locationId?, planting?, job?, lang, unit, days, device };
 * PATCH /todo/own/:id { title?, when?, day?, job?, lang, unit, days, device } (when only when a chip was touched).
 * 400 TODO_BAD_REQUEST, 404 TODO_JOB_NOT_FOUND (edit), 400 ZIP_REQUIRED.
 *
 * @module api/ownTodo
 */

import { apiClient } from './apiClient';
import type { TodoAsk, TodoSaid } from './todo';

/** When a to-do is for, as the sheet's chips say it: today, the next seven days (under When you can), or a day. */
export type OwnWhen = 'today' | 'week' | 'day';

/** What a to-do is about: the garden as a whole, one plant, or one bed. */
export type OwnAbout = 'garden' | 'plant' | 'bed';

/** A new to-do, as the add sheet sends it. */
export interface OwnTodoNew {
  /** As the gardener typed or said it; the server trims it and never translates it. */
  title: string;
  when: OwnWhen;
  /** For when 'day': "YYYY-MM-DD", today or later. */
  day?: string;
  about: OwnAbout;
  /** For about 'plant'. */
  plantObjId?: number;
  /** For about 'bed'. */
  locationId?: number;
  /** E23: a planting to-do (about 'plant'; since E25 the server makes these itself with "Plant something", api/plantSomething): the server ticks it as the plant's
   * planting and says when it suggests planting. */
  planting?: boolean;
  /** B140: a code on the shared Jobs list for what the to-do is about; left out when the gardener tapped no job pill. */
  job?: string;
}

/** A change to a to-do, as the edit sheet sends it: the title, and the when only when a chip was touched. */
export interface OwnTodoChange {
  title?: string;
  when?: OwnWhen;
  day?: string;
  /** B140: the job tapped (a code), or null to drop it; left out when no job pill was touched (the server then leaves it). */
  job?: string | null;
}

/** What the server says after an own to-do's write: the toast and its Undo, and the key its group shows and scrolls to. */
export interface OwnTodoSaid extends TodoSaid {
  /** The to-do's key in the list (the server works it out from its day); null when it has none. */
  openKey?: string | null;
}

/** An own to-do write's answer (B73): saved, and what to say; the list is read again by the page. */
export interface OwnTodoAnswer {
  success: boolean;
  /** True once the write is saved. */
  saved: boolean;
  said?: OwnTodoSaid;
}

/**
 * Adds a to-do and answers, once it is saved, with what to say (no brief).
 *
 * @param todo - The title, when, and what it is about.
 * @param ask - How the brief was asked for (language, unit, Coming up's days, device).
 * @returns Saved, with said.
 */
export async function addOwnTodo(todo: OwnTodoNew, ask: TodoAsk): Promise<OwnTodoAnswer> {
  const response = await apiClient.post<OwnTodoAnswer>('/todo/own', { ...todo, lang: ask.lang, unit: ask.unit, days: ask.days, device: ask.device });
  return response.data;
}

/**
 * Changes a to-do's title or when, and answers once it is saved with what to say ("Saved."; no brief).
 *
 * @param id - The to-do (own_todo_id).
 * @param change - What changed.
 * @param ask - How the brief was asked for.
 * @returns Saved, with said.
 */
export async function editOwnTodo(id: number, change: OwnTodoChange, ask: TodoAsk): Promise<OwnTodoAnswer> {
  const response = await apiClient.patch<OwnTodoAnswer>(`/todo/own/${id}`, { ...change, lang: ask.lang, unit: ask.unit, days: ask.days, device: ask.device });
  return response.data;
}
